import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton, Select, Stack, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { getTodayDateFormatted } from 'helpers/helpers';
import { DateTime } from 'luxon';
import { TimeInMS } from 'types/time';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GenericModal from 'components/GenericModal';

const DATE_RANGE_VALUE = 'Dates range';

const parseDate = (value, fallback) => {
  if (!value) return fallback;
  const parsed = DateTime.fromFormat(value, 'dd.MM.yyyy');
  return parsed.isValid ? parsed : fallback;
};

const findOptionByValue = (options, value) =>
  options?.find(option => String(option.value) === String(value)) ?? null;

const inferOptionFromRange = (range, options) => {
  if (!range) return options?.[0] ?? { label: '', value: '' };

  if (range.value !== undefined && range.value !== null) {
    return (
      findOptionByValue(options, range.value) ?? { label: range.label ?? '', value: range.value }
    );
  }

  const startDate = parseDate(range.startDate, null);
  const endDate = parseDate(range.endDate, null);
  if (!startDate || !endDate) {
    return options?.[0] ?? { label: '', value: '' };
  }

  const today = DateTime.now().startOf('day');
  if (startDate.hasSame(today, 'day') && endDate.hasSame(today, 'day')) {
    return findOptionByValue(options, TimeInMS.Day) ?? { label: 'Today', value: TimeInMS.Day };
  }
  if (startDate.hasSame(today.minus({ days: 7 }), 'day') && endDate.hasSame(today, 'day')) {
    return findOptionByValue(options, TimeInMS.Week) ?? { label: '1 Week', value: TimeInMS.Week };
  }
  if (startDate.hasSame(today.minus({ months: 1 }), 'day') && endDate.hasSame(today, 'day')) {
    return (
      findOptionByValue(options, TimeInMS.Month) ?? { label: '1 Month', value: TimeInMS.Month }
    );
  }

  return (
    findOptionByValue(options, DATE_RANGE_VALUE) ?? {
      label: DATE_RANGE_VALUE,
      value: DATE_RANGE_VALUE,
    }
  );
};

/**
 * A button that opens a dialog containing a select (dropdown)
 * @param {*} props
 * @returns
 */
export default function HomePageDialogSelect(props) {
  const [open, setOpen] = useState(false);
  const rangeObj = useSelector(state => state.homePage.homePageDatesObj);
  const selectOptions = useMemo(() => {
    if (props.selectOptions?.some(option => option.value === TimeInMS.Hour)) {
      return props.selectOptions;
    }

    return [{ label: '1 Hour', value: TimeInMS.Hour }, ...(props.selectOptions ?? [])];
  }, [props.selectOptions]);

  const initialRange = rangeObj || props.default || {};
  const defaultStartDate = initialRange.startDate || getTodayDateFormatted();
  const defaultEndDate = initialRange.endDate || getTodayDateFormatted();
  const defaultOption = inferOptionFromRange(initialRange, selectOptions);

  const [localStartDate, setLocalStartDate] = useState(defaultStartDate);
  const [localEndDate, setLocalEndDate] = useState(defaultEndDate);
  const [tempSelectedOptionType, setTempSelectedOptionType] = useState(defaultOption);
  const [finalSelectedOptionType, setFinalSelectedOptionType] = useState(defaultOption);

  const timeRangesListRef = useRef(null);

  const [selectedStartDate, setSelectedStartDate] = useState(() =>
    parseDate(defaultStartDate, DateTime.now().startOf('day')),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(() =>
    parseDate(defaultEndDate, DateTime.now().startOf('day')),
  );
  const [endDateMaxDate, setEndDateMaxDate] = useState(DateTime.now());

  const minDate = DateTime.now().minus({ days: 60 });
  const startDateMaxDate = DateTime.now();
  const dispatch = useDispatch();

  const handleChange = event => {
    const selected = event.target.value;
    const selectedOption = findOptionByValue(selectOptions, selected) ?? {
      label: selected,
      value: selected,
    };

    const now = DateTime.now().startOf('day');
    if (selected === String(TimeInMS.Day)) {
      setSelectedStartDate(now);
      setSelectedEndDate(now);
    }
    if (selected === String(TimeInMS.Week)) {
      setSelectedStartDate(now.minus({ days: 7 }));
      setSelectedEndDate(now);
    }
    if (selected === String(TimeInMS.Month)) {
      setSelectedStartDate(now.minus({ months: 1 }));
      setSelectedEndDate(now);
    }

    setTempSelectedOptionType(selectedOption);
  };

  const handleClickOpen = () => {
    setTempSelectedOptionType(finalSelectedOptionType);
    setSelectedStartDate(parseDate(localStartDate, DateTime.now().startOf('day')));
    setSelectedEndDate(parseDate(localEndDate, DateTime.now().startOf('day')));
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    setTempSelectedOptionType(finalSelectedOptionType);
    setOpen(false);
  };

  const handleOK = async reason => {
    setOpen(false);
    if (reason !== 'backdropClick') {
      if (selectedStartDate === null || selectedEndDate === null) {
        alert('please select date range');
        return;
      }

      const selectedOption = findOptionByValue(
        selectOptions,
        tempSelectedOptionType?.value,
      ) ?? {
        label: tempSelectedOptionType?.label ?? '',
        value: tempSelectedOptionType?.value ?? '',
      };

      const selectedDates = {
        startDate: selectedStartDate.toFormat('dd.MM.yyyy'),
        endDate: selectedEndDate.toFormat('dd.MM.yyyy'),
        label: selectedOption.label,
        value: selectedOption.value,
      };

      setFinalSelectedOptionType(selectedOption);
      dispatch(props.onSelectionOK(selectedDates));
      setLocalStartDate(selectedDates.startDate);
      setLocalEndDate(selectedDates.endDate);
    }
  };

  const handleChangeStartDate = nextSelectedStartDate => {
    if (!nextSelectedStartDate) return;

    const todayMax = DateTime.local();
    const thirtyOneDaysFromSelectedStartDate = nextSelectedStartDate.plus({ days: 31 });
    let newMaxEndDate;

    if (thirtyOneDaysFromSelectedStartDate < todayMax) {
      setEndDateMaxDate(thirtyOneDaysFromSelectedStartDate);
      newMaxEndDate = thirtyOneDaysFromSelectedStartDate;
    } else {
      setEndDateMaxDate(todayMax);
      newMaxEndDate = todayMax;
    }

    if (selectedEndDate && selectedEndDate > newMaxEndDate) {
      setSelectedEndDate(nextSelectedStartDate);
    }
    if (selectedEndDate && nextSelectedStartDate > selectedEndDate) {
      setSelectedEndDate(nextSelectedStartDate);
    }

    setSelectedStartDate(nextSelectedStartDate);
  };

  const getOptions = () => {
    return selectOptions.map(option => {
      return (
        <option value={option.value ?? 0} key={option.value}>
          {option.label ?? ''}
        </option>
      );
    });
  };

  useEffect(() => {
    const sourceRange = rangeObj || props.default;
    if (!sourceRange) return;

    const nextStartDate = sourceRange.startDate || getTodayDateFormatted();
    const nextEndDate = sourceRange.endDate || getTodayDateFormatted();
    const optionFromStore = inferOptionFromRange(sourceRange, selectOptions);

    setLocalStartDate(nextStartDate);
    setLocalEndDate(nextEndDate);
    setFinalSelectedOptionType(optionFromStore);

    if (!open) {
      setTempSelectedOptionType(optionFromStore);
    }

    setSelectedStartDate(parseDate(nextStartDate, DateTime.now().startOf('day')));
    setSelectedEndDate(parseDate(nextEndDate, DateTime.now().startOf('day')));
  }, [rangeObj, props.default, selectOptions, open]);

  const today = getTodayDateFormatted();
  const localStartDateTime = parseDate(localStartDate, null);
  const localEndDateTime = parseDate(localEndDate, null);
  const displayedDateRange =
    localStartDateTime?.hasSame(localEndDateTime, 'month')
      ? `${localStartDateTime.toFormat('dd')} - ${localEndDateTime.toFormat('dd.MM.yy')}`
      : `${localStartDate} - ${localEndDate}`;
  const selectedRangeLabel = finalSelectedOptionType?.label ?? props.buttonSubTitle ?? 'Today';
  const isCustomDateRange = finalSelectedOptionType?.value === DATE_RANGE_VALUE;

  const dialogTitleNode = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span">{props.dialogTitle ?? ''}</Box>
      {props.toolTip && (
        <Tooltip
          title={props.toolTip}
          slotProps={{
            popper: {
              sx: { zIndex: 50020 },
            },
          }}>
          <IconButton size="small" sx={{ color: 'grey', p: 0.5 }}>
            <InfoIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const HomePageDialogSelectContent = () => (
    <>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}>
        <FormControl sx={{ m: 1, minWidth: 120, flex: 1 }}>
          <InputLabel>{props.selectLabel ?? ''}</InputLabel>
          <Select
            ref={timeRangesListRef}
            native
            value={tempSelectedOptionType?.value ?? ''}
            onChange={handleChange}
            input={<OutlinedInput label={props.selectLabel ?? ''} />}>
            {getOptions()}
          </Select>
        </FormControl>
        <IconButton
          aria-label="ok"
          onClick={handleOK}
          sx={{
            m: 1,
            '&:hover': {
              backgroundColor: 'rgba(225, 225, 225, 1)',
              color: 'black',
            },
          }}>
          <DoneIcon />
        </IconButton>
      </Box>
      {tempSelectedOptionType?.value === DATE_RANGE_VALUE && (
        <Stack sx={{ m: 1, gap: 2 }}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              sx={{
                // marginTop: 1,
                width: 205,
              }}
              label="Start date"
              value={selectedStartDate}
              onChange={handleChangeStartDate}
              minDate={minDate}
              maxDate={startDateMaxDate}
              slots={{ textField: TextField }}
              slotProps={{
                textField: { variant: 'outlined' },
                popper: { sx: { zIndex: 50010 } },
              }}
              format="dd.MM.yyyy"
            />
            <DatePicker
              sx={{
                // margin: 1,
                width: 205,
              }}
              label="End date"
              value={selectedEndDate}
              onChange={setSelectedEndDate}
              minDate={selectedStartDate}
              maxDate={endDateMaxDate}
              slots={{ textField: TextField }}
              slotProps={{
                textField: { variant: 'outlined' },
                popper: { sx: { zIndex: 50010 } },
              }}
              format="dd.MM.yyyy"
            />
          </LocalizationProvider>
        </Stack>
      )}
    </>
  );

  return (
    <div>
      <MDButton color={'white'} variant="text" onClick={handleClickOpen}>
        <Stack>
          <MDTypography variant="button" fontWeight="bold" color={props.color}>
            {props.buttonTitle ?? ''}
          </MDTypography>
          {!isCustomDateRange && (
            <MDTypography variant="caption" fontWeight="light" color={props.color}>
              {selectedRangeLabel}
            </MDTypography>
          )}
          {isCustomDateRange && localStartDate !== localEndDate && localStartDate !== today && (
            <MDTypography variant="caption" fontWeight="light" color={props.color}>
              {displayedDateRange}
            </MDTypography>
          )}

          {isCustomDateRange && localStartDate === localEndDate && localStartDate !== today && (
            <MDTypography variant="caption" fontWeight="light" color={props.color}>
              {localStartDate}
            </MDTypography>
          )}

          {isCustomDateRange && localStartDate === localEndDate && localStartDate === today && (
            <MDTypography variant="caption" fontWeight="light" color={props.color}>
              Today
            </MDTypography>
          )}
        </Stack>
      </MDButton>
      <GenericModal
        open={open}
        onClose={handleClose}
        title={dialogTitleNode}
        Component={HomePageDialogSelectContent}
        contentProps={{
          dividers: true,
          sx: {
            p: 2,
            borderBottom: 'none !important',
          },
        }}
        closeOnBackdrop={true}
        closeOnEsc={true}
        paperSx={{ minHeight: 'fit-content' }}
      />
    </div>
  );
}
